import type { ContentRating } from "./common.js";

/**
 * RoQeet Hub 도메인 모델 — 결정 #23/#31.
 *
 * 허브는 **로컬 우선(결정 #1)** 원칙을 깨지 않는 선택적 레이어다: 익명 사용자는
 * 허브 없이도 100% 기능을 쓰고, 허브는 "내가 만든 것을 올리고 / 남이 만든 것을
 * 받아오는" 교환 지점일 뿐이다. 그래서 이 모델은 두 축으로 설계된다.
 *
 *  1) **콘텐츠 배포**(결정 #23): 캐릭터·로어북·프리셋·게임 카드를 업로드·검색·평점·
 *     태그·다운로드. 페이로드는 기존 import/export JSON(결정 #6)을 그대로 싣는다 —
 *     즉 허브는 "교환 포맷의 원격 저장소"이고, 받는 쪽은 평소처럼 import 경로를 탄다.
 *  2) **소셜 레이어**(결정 #31): 프로필·팔로우·댓글·평점.
 *
 * 콘텐츠 정책(결정 #23/#31): 연령 게이트 + NSFW 허용·분리 노출. {@link ContentRating}을
 * 재사용하고, 목록 조회는 기본적으로 SFW만 반환하도록 질의에서 게이팅한다.
 *
 * 이 파일은 플랫폼 중립(결정 #10): DOM/네트워크 의존이 전혀 없는 순수 데이터 모양만
 * 정의한다. 실제 백엔드 호출은 {@link HubClient} 포트의 web 어댑터(Supabase)가 한다.
 */

/** 허브에 올릴 수 있는 콘텐츠 종류(모두 ShareMeta를 가진 엔티티, 결정 #23). */
export type HubContentKind = "character" | "lorebook" | "preset" | "game-card";

export type HubItemKind =
  | "game"
  | "bot"
  | "persona"
  | "lorebook"
  | "setting_preset"
  | "save_file";

export type BotSubtype =
  | "character"
  | "scenario"
  | "assistant"
  | "multi_character"
  | "npc"
  | "utility"
  | (string & {});

export type HubItemStatus =
  | "draft"
  | "published"
  | "hidden"
  | "reported"
  | "rejected"
  | "deleted";

export type HubItemVisibility = "public" | "unlisted" | "private";

export interface HubItemCreatorDto {
  id: string;
  displayName: string;
  handle?: string | null;
  avatarUrl?: string | null;
}

export type HubAuthState = "anonymous" | "member";

export interface HubMemberDto {
  id: string;
  displayName: string;
  handle?: string | null;
  avatarUrl?: string | null;
}

export interface HubSessionDto {
  state: HubAuthState;
  member?: HubMemberDto | null;
}

export interface HubPublicProfileDto {
  id: string;
  displayName: string;
  handle?: string | null;
  avatarUrl?: string | null;
  bio?: string | null;
  createdAt?: string | null;
}

export interface HubItemStatsDto {
  views?: number;
  downloads?: number;
  installs?: number;
  ratingAverage?: number | null;
  ratingCount?: number;
  comments?: number;
  bookmarks?: number;
}

export interface HubItemParentSummaryDto {
  id: string;
  kind: HubItemKind;
  botSubtype?: BotSubtype | null;
  title: string;
  slug?: string | null;
}

export const HUB_CREATOR_HANDLE_PATTERN =
  /^[a-z0-9](?:[a-z0-9._-]{1,30}[a-z0-9])?$/;

export const HUB_CREATOR_UNKNOWN_DISPLAY_NAME = "Unknown creator";

export const HUB_ITEM_STATS_DEFAULTS: Required<HubItemStatsDto> = {
  views: 0,
  downloads: 0,
  installs: 0,
  ratingAverage: null,
  ratingCount: 0,
  comments: 0,
  bookmarks: 0,
};

export function normalizeHubCreatorHandle(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim().replace(/^@+/, "").toLowerCase();
  return HUB_CREATOR_HANDLE_PATTERN.test(normalized) ? normalized : null;
}

export function isHubCreatorHandle(value: unknown): value is string {
  return typeof value === "string" && normalizeHubCreatorHandle(value) === value;
}

export function fallbackHubCreatorDisplayName(ownerId: string | null | undefined): string {
  const id = typeof ownerId === "string" ? ownerId.trim() : "";
  if (!id) return HUB_CREATOR_UNKNOWN_DISPLAY_NAME;
  return id.length > 12 ? `${id.slice(0, 8)}...${id.slice(-4)}` : id;
}

function normalizeDisplayName(
  value: string | null | undefined,
  fallback: string,
): string {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed || fallback;
}

function publicHttpsUrl(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    const url = new URL(trimmed);
    return url.protocol === "https:" ? url.toString() : null;
  } catch {
    return null;
  }
}

export function normalizeHubItemCreator(
  ownerId: string,
  creator?: Partial<HubItemCreatorDto> | null,
): HubItemCreatorDto {
  const id = normalizeDisplayName(creator?.id, ownerId);
  const handle = normalizeHubCreatorHandle(creator?.handle);
  const displayName = normalizeDisplayName(
    creator?.displayName,
    handle ?? fallbackHubCreatorDisplayName(id),
  );
  const avatarUrl = publicHttpsUrl(creator?.avatarUrl);
  return {
    id,
    displayName,
    handle,
    avatarUrl,
  };
}

export function normalizeHubMember(
  id: string,
  member?: Partial<HubMemberDto> | null,
): HubMemberDto {
  return normalizeHubItemCreator(id, member);
}

export function anonymousHubSession(): HubSessionDto {
  return { state: "anonymous", member: null };
}

export function memberHubSession(member: HubMemberDto): HubSessionDto {
  return { state: "member", member: normalizeHubMember(member.id, member) };
}

export function publicProfileFromHubProfile(profile: HubProfile): HubPublicProfileDto {
  const member = normalizeHubMember(profile.id, {
    id: profile.id,
    displayName: profile.handle,
    handle: profile.handle,
    avatarUrl: profile.avatarUrl,
  });
  return {
    id: member.id,
    displayName: member.displayName,
    handle: member.handle,
    avatarUrl: member.avatarUrl,
    ...(profile.bio !== undefined ? { bio: profile.bio } : {}),
    createdAt: new Date(profile.createdAt).toISOString(),
  };
}

export function normalizeHubPublicProfile(
  profile: Partial<HubPublicProfileDto> & Pick<HubPublicProfileDto, "id">,
): HubPublicProfileDto {
  const member = normalizeHubMember(profile.id, profile);
  return {
    id: member.id,
    displayName: member.displayName,
    handle: member.handle,
    avatarUrl: member.avatarUrl,
    ...(profile.bio !== undefined ? { bio: profile.bio } : {}),
    ...(profile.createdAt !== undefined ? { createdAt: profile.createdAt } : {}),
  };
}

export function normalizeHubItemStats(stats?: HubItemStatsDto | null): Required<HubItemStatsDto> {
  return {
    views: stats?.views ?? HUB_ITEM_STATS_DEFAULTS.views,
    downloads: stats?.downloads ?? HUB_ITEM_STATS_DEFAULTS.downloads,
    installs: stats?.installs ?? HUB_ITEM_STATS_DEFAULTS.installs,
    ratingAverage: stats?.ratingAverage ?? HUB_ITEM_STATS_DEFAULTS.ratingAverage,
    ratingCount: stats?.ratingCount ?? HUB_ITEM_STATS_DEFAULTS.ratingCount,
    comments: stats?.comments ?? HUB_ITEM_STATS_DEFAULTS.comments,
    bookmarks: stats?.bookmarks ?? HUB_ITEM_STATS_DEFAULTS.bookmarks,
  };
}

export function normalizeHubItemDto(item: HubItemDto): HubItemDto {
  const normalized: HubItemDto = {
    id: item.id,
    ownerId: item.ownerId,
    creator: normalizeHubItemCreator(item.ownerId, item.creator),
    kind: item.kind,
    title: item.title,
    status: item.status,
    stats: normalizeHubItemStats(item.stats),
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  };
  if (item.botSubtype !== undefined) normalized.botSubtype = item.botSubtype;
  if (item.parentItemId !== undefined) normalized.parentItemId = item.parentItemId;
  if (item.parent !== undefined) {
    normalized.parent = item.parent
      ? {
          id: item.parent.id,
          kind: item.parent.kind,
          ...(item.parent.botSubtype !== undefined ? { botSubtype: item.parent.botSubtype } : {}),
          title: item.parent.title,
          ...(item.parent.slug !== undefined ? { slug: item.parent.slug } : {}),
        }
      : null;
  }
  if (item.slug !== undefined) normalized.slug = item.slug;
  if (item.summary !== undefined) normalized.summary = item.summary;
  if (item.description !== undefined) normalized.description = item.description;
  if (item.tags !== undefined) normalized.tags = item.tags;
  if (item.thumbnailUrl !== undefined) normalized.thumbnailUrl = publicHttpsUrl(item.thumbnailUrl);
  if (item.rating !== undefined) normalized.rating = item.rating;
  if (item.visibility !== undefined) normalized.visibility = item.visibility;
  if (item.publishedAt !== undefined) normalized.publishedAt = item.publishedAt;
  return normalized;
}

// Canonical read/display DTO for RoQeet Hub. This does not imply a legacy install payload exists.
export interface HubItemDto {
  id: string;
  ownerId: string;
  creator?: HubItemCreatorDto | null;
  kind: HubItemKind;
  botSubtype?: BotSubtype | null;
  parentItemId?: string | null;
  parent?: HubItemParentSummaryDto | null;
  title: string;
  slug?: string | null;
  summary?: string | null;
  description?: string | null;
  tags?: string[];
  thumbnailUrl?: string | null;
  rating?: string | null;
  status: HubItemStatus;
  visibility?: HubItemVisibility;
  stats?: HubItemStatsDto;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string | null;
}

export const HUB_ITEM_KINDS: readonly HubItemKind[] = [
  "game",
  "bot",
  "persona",
  "lorebook",
  "setting_preset",
  "save_file",
] as const;

export function isHubItemKind(value: unknown): value is HubItemKind {
  return typeof value === "string" && (HUB_ITEM_KINDS as readonly string[]).includes(value);
}

export function hubItemKindFromLegacyContentKind(kind: HubContentKind): HubItemKind {
  switch (kind) {
    case "character":
      return "bot";
    case "lorebook":
      return "lorebook";
    case "preset":
      return "setting_preset";
    case "game-card":
      return "game";
  }
}

export function legacyContentKindFromHubItemKind(kind: HubItemKind): HubContentKind | undefined {
  switch (kind) {
    case "game":
      return "game-card";
    case "bot":
    case "persona":
      return "character";
    case "lorebook":
      return "lorebook";
    case "setting_preset":
      return "preset";
    case "save_file":
      return undefined;
  }
}

export interface HubItemDisplayInput {
  kind?: HubContentKind;
  itemKind?: HubItemKind;
  botSubtype?: BotSubtype | null;
  parentItemId?: string | null;
}

export function hubItemKindForDisplay(item: HubItemDisplayInput): HubItemKind {
  return item.itemKind ?? hubItemKindFromLegacyContentKind(item.kind ?? "character");
}

export function getHubItemDisplayLabel(
  item: HubItemDisplayInput,
  locale: string = "en-US",
): string {
  const ko = locale.toLowerCase().startsWith("ko");
  const itemKind = hubItemKindForDisplay(item);
  switch (itemKind) {
    case "game":
      return ko ? "게임" : "Game";
    case "bot":
      switch (item.botSubtype) {
        case "character":
          return ko ? "캐릭터봇" : "Character Bot";
        case "scenario":
          return ko ? "시나리오봇" : "Scenario Bot";
        case "assistant":
          return ko ? "도우미봇" : "Assistant Bot";
        case "multi_character":
          return ko ? "멀티캐릭터봇" : "Multi-character Bot";
        case "npc":
          return ko ? "NPC봇" : "NPC Bot";
        case "utility":
          return ko ? "유틸리티봇" : "Utility Bot";
        default:
          return ko ? "챗봇" : "Bot";
      }
    case "persona":
      return ko ? "페르소나" : "Persona";
    case "lorebook":
      return ko ? "로어북" : "Lorebook";
    case "setting_preset":
      return ko ? "세팅 프리셋" : "Setting Preset";
    case "save_file":
      return ko ? "세이브 파일" : "Save File";
  }
}

export function getHubItemParentDependencyLabel(
  item: HubItemDisplayInput,
  locale: string = "en-US",
): string | undefined {
  if (hubItemKindForDisplay(item) !== "save_file" || item.parentItemId) return undefined;
  return locale.toLowerCase().startsWith("ko") ? "원본 항목 필요" : "Parent item required";
}

/** 목록 정렬 기준. */
export function hubItemDtoFromListing(listing: HubListing): HubItemDto {
  return {
    id: listing.id,
    ownerId: listing.author.id,
    creator: normalizeHubItemCreator(listing.author.id, {
      id: listing.author.id,
      handle: listing.author.handle,
      displayName: listing.author.handle,
      avatarUrl: listing.author.avatarUrl,
    }),
    kind: hubItemKindForDisplay(listing),
    ...(listing.botSubtype !== undefined ? { botSubtype: listing.botSubtype } : {}),
    ...(listing.parentItemId !== undefined ? { parentItemId: listing.parentItemId } : {}),
    title: listing.title,
    ...(listing.summary !== undefined ? { summary: listing.summary } : {}),
    ...(listing.description !== undefined ? { description: listing.description } : {}),
    tags: listing.tags,
    ...(listing.thumbnailUrl !== undefined ? { thumbnailUrl: publicHttpsUrl(listing.thumbnailUrl) } : {}),
    rating: listing.rating,
    status: listing.itemStatus ?? "published",
    ...(listing.itemVisibility !== undefined ? { visibility: listing.itemVisibility } : {}),
    stats: normalizeHubItemStats({
      views: listing.viewCount,
      downloads: listing.downloadCount,
      ratingAverage: listing.ratingAvg,
      ratingCount: listing.ratingCount,
      comments: listing.commentCount,
      bookmarks: listing.bookmarkCount,
    }),
    createdAt: new Date(listing.createdAt).toISOString(),
    updatedAt: new Date(listing.updatedAt).toISOString(),
  };
}

export type HubSort = "recent" | "popular" | "top-rated" | "downloads" | "views" | "bookmarks";

/** 허브 사용자(플랫폼 계정, 결정 #31 tier 3). 신원은 Google 재활용. */
export interface HubProfile {
  /** 안정적 사용자 식별자(백엔드 계정 id). */
  id: string;
  /** 표시 이름(핸들). */
  handle: string;
  /** 자기소개. */
  bio?: string;
  /** 아바타 이미지 URL(허브 스토리지). */
  avatarUrl?: string;
  /** 가입 시각(epoch ms). */
  createdAt: number;
  /** 집계: 올린 콘텐츠 수. */
  listingCount?: number;
  /** 집계: 팔로워 수. */
  followerCount?: number;
  /** 집계: 팔로잉 수. */
  followingCount?: number;
  /** 현재 사용자가 이 프로필을 팔로우 중인지(뷰어 컨텍스트). */
  viewerFollows?: boolean;
}

/**
 * 허브에 발행된 콘텐츠 한 건의 목록 메타데이터. 무거운 페이로드(엔티티 JSON·썸네일
 * 바이트)는 포함하지 않는다 — 목록 조회가 6s+ 메모리에 안전하도록(결정 #9 정신).
 * 실제 설치는 {@link HubClient.download}로 페이로드를 따로 받는다.
 */
// Legacy install/download compatibility model. Prefer HubItemDto for read-only Hub display.
export interface HubListing {
  /** 발행물의 안정적 식별자. */
  id: string;
  kind: HubContentKind;
  itemKind?: HubItemKind;
  botSubtype?: BotSubtype | null;
  parentItemId?: string | null;
  itemStatus?: HubItemStatus;
  itemVisibility?: HubItemVisibility;

  title: string;
  summary?: string;
  /** 마크다운 상세 설명(상세 화면용). */
  description?: string;

  /** 발행자 프로필(요약). */
  author: Pick<HubProfile, "id" | "handle" | "avatarUrl">;

  /** 발견용 태그. */
  tags: string[];
  /** 콘텐츠 등급(결정 #23 연령 게이트). */
  rating: ContentRating;

  /** 썸네일/커버 이미지 URL(허브 스토리지). */
  thumbnailUrl?: string;

  /** 제작자가 붙인 버전 문자열. */
  version?: string;
  /** SPDX 유사 라이선스. */
  license?: string;

  // ---- 집계(읽기 전용, 백엔드가 계산) ----
  downloadCount: number;
  /** 평점 평균(0~5), 평가가 없으면 null. */
  ratingAvg: number | null;
  ratingCount: number;
  commentCount: number;
  viewCount: number;
  bookmarkCount: number;
  /** 소스코드 공개 여부. 'hidden'이면 플레이만 가능하고 소스 편집 불가. */
  sourceVisibility: "public" | "hidden";
  /** 통계 공개 설정. 'private'는 제작자만 조회, 'uncollected'는 수집 자체 안 함. */
  statsPrivacy: "public" | "private" | "uncollected";

  createdAt: number;
  updatedAt: number;

  // ---- 뷰어 컨텍스트(로그인 사용자 기준) ----
  /** 현재 사용자가 매긴 평점(1~5), 없으면 null. */
  viewerRating?: number | null;
  /** 현재 사용자가 이미 설치했는지(publicId 매칭). */
  viewerInstalled?: boolean;
  /** 현재 사용자가 이 발행물을 북마크했는지. */
  viewerBookmarked?: boolean;
}

/** 설치를 위해 받아오는 발행물 전체(목록 메타 + 교환 포맷 페이로드). */
export interface HubListingDetail extends HubListing {
  /**
   * 엔티티 교환 포맷 JSON(결정 #6). kind에 맞는 importer로 도메인 엔티티가 된다:
   *  - character  -> importCardFromJson 계열
   *  - lorebook   -> importLorebookFromJson
   *  - preset     -> importPresetFromJson
   *  - game-card  -> importGameCardFromJson
   */
  payload: string;
}

/** 발행 요청(업로드). 페이로드는 호출부가 export 함수로 직렬화해 넘긴다(결정 #6). */
export interface HubPublishInput {
  kind: HubContentKind;
  title: string;
  summary?: string;
  description?: string;
  tags: string[];
  rating: ContentRating;
  version?: string;
  license?: string;
  /** 소스코드 공개 여부 (기본 'public'). */
  sourceVisibility?: "public" | "hidden";
  /** 통계 공개 설정 (기본 'public'). */
  statsPrivacy?: "public" | "private" | "uncollected";
  /** 엔티티 교환 포맷 JSON 문자열(결정 #6). */
  payload: string;
  /** 썸네일 이미지(선택). 어댑터가 허브 스토리지에 올린다. */
  thumbnail?: Blob;
  /**
   * 같은 작품의 재발행이면 이전 publicId. 새 버전으로 갱신하며, 설치한 사용자가
   * 업데이트를 감지할 수 있게 한다(결정 #23 ShareMeta.publicId).
   */
  publicId?: string;
}

/** 한 건의 댓글(결정 #31 소셜 레이어). */
export interface HubComment {
  id: string;
  listingId: string;
  author: Pick<HubProfile, "id" | "handle" | "avatarUrl">;
  body: string;
  createdAt: number;
  /** 작성자 본인이면 true(수정/삭제 UI 노출용). */
  viewerIsAuthor?: boolean;
}

/** 목록/검색 질의(결정 #23 검색·태그·등급 게이팅). */
export interface HubQuery {
  /** 자유 텍스트 검색(제목/요약/태그). */
  text?: string;
  itemKind?: HubItemKind;
  botSubtype?: BotSubtype;
  kind?: HubContentKind;
  /** 특정 태그로 필터. */
  tag?: string;
  /** 특정 작성자의 발행물만. */
  authorId?: string;
  sort?: HubSort;
  /**
   * NSFW 포함 여부(결정 #23/#31). 기본 false — SFW만 노출. true는 연령 인증
   * 통과 사용자에게만 호출부가 허용해야 한다(결정 #31 게이팅).
   */
  includeNsfw?: boolean;
  /** 커서(이전 페이지의 마지막 항목 정렬키). 페이지네이션용. */
  cursor?: string;
  /** 페이지 크기(기본 어댑터가 정함). */
  limit?: number;
}

/** 커서 기반 페이지 결과(결정 #9: 한 번에 다 안 부름). */
export interface HubPage<T> {
  items: T[];
  /** 다음 페이지 커서. 없으면 마지막 페이지. */
  nextCursor?: string;
}
